import { createColumnHelper } from "@tanstack/react-table";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Icon } from "semantic-ui-react";

import { Can, DATE_FORMATS_FNS, NavPortal, ReactTable, SearchFilter, useCan, usePermanentRealtimeDealers } from "components";
import { useRegisterNavAction } from "hooks";
import { ACL_ACTIVITIES, ACL_RESOURCES, DMSIDS } from "models";
import { DMSv3LocationsTable } from "modules/DMSv3/Settings/components/DMSv3SettingsTable/components";
import "modules/DMSv3/Settings/components/DMSv3SettingsTable/DMSv3SettingsTable.scss";
import { useDMSv3DealersListQuery } from "modules/DMSv3/Settings/components/DMSv3SettingsTable/hooks";
import { PAGE_SIZE } from "utils/common";
import { formatDate } from "utils/dateHelperFunctions";
import { getDmsName } from "utils/dms";

import type { DealerDMS, DealerLocation } from "models";

type DMSv3SettingsTableProps = {
  onSelectDealer: (dealer: DealerDMS) => void;
  onSelectLocation: (location: DealerLocation | null) => void;
  onSelectLocationDealer: (dealer: DealerDMS) => void;
};

const columnHelper = createColumnHelper<DealerDMS>();

export const DMSv3SettingsTable = ({ onSelectDealer, onSelectLocation, onSelectLocationDealer }: DMSv3SettingsTableProps) => {
  const { t } = useTranslation();

  const [searchTerm, setSearchTerm] = useState("");
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean> | undefined>(undefined);

  const canListDmsDealers = useCan(ACL_ACTIVITIES.List, ACL_RESOURCES.DMSv3SettingDealers);
  const { dealers } = usePermanentRealtimeDealers();
  const { data: dealersList, refetch: refetchDealersList, isFetching: isFetchingDealers } = useDMSv3DealersListQuery();
  useRegisterNavAction(() => canListDmsDealers && refetchDealersList());

  const filteredDealerList = dealersList?.filter(d => d.dealer_name?.toLowerCase().includes(searchTerm.toLowerCase()));

  useEffect(() => {
    setExpandedRows(prev => {
      if (!prev) return prev;

      const expandedRowIds = Object.entries(prev).map(([id]) => +id);
      const dealerLocations = expandedRowIds.filter(rowId => filteredDealerList?.[rowId]?.dealer_locations?.length);

      const someRowsWereRemoved = dealerLocations.length !== expandedRowIds.length;
      if (!someRowsWereRemoved) return prev;

      return Object.fromEntries(dealerLocations.map(rowId => [rowId, prev[rowId]]));
    });
  }, [filteredDealerList]);

  const columns = useMemo(
    () => [
      columnHelper.accessor(row => row, {
        id: "dealer",
        header: () => <span className="no-wrap-text">{t("v8_dealer", "Dealer")}</span>,
        size: 100,
        cell: ({ row: { original, toggleExpanded, getIsExpanded } }) => {
          const hasDealerLocations = original.dealer_locations && original.dealer_locations.length > 0;

          return (
            <div className="table-name-container">
              {hasDealerLocations && (
                <button
                  className="table-expand-button"
                  onClick={e => {
                    e.stopPropagation();
                    toggleExpanded();
                  }}
                >
                  <div className="collapse-container">
                    <Icon className={`caret ${getIsExpanded() ? "down" : "right"}`} />
                  </div>
                </button>
              )}
              <span className={!hasDealerLocations ? "dealer-text" : ""}>{original.dealer_name || ""}</span>
            </div>
          );
        }
      }),
      columnHelper.accessor(row => row, {
        id: "dms",
        header: () => <span className="no-wrap-text">{t("v8_dms", "DMS")}</span>,
        size: 80,
        cell: ({ row: { original } }) => <span className="no-wrap-text">{getDmsName(original.dms_id)}</span>
      }),
      columnHelper.accessor(row => row, {
        id: "next_call_at",
        header: () => <span className="no-wrap-text">{t("v8_next_call_at", "Next Call At")}</span>,
        size: 80,
        cell: ({ row: { original } }) => <span className="no-wrap-text">{formatDate(original.next_call_at, DATE_FORMATS_FNS.dateMonthYearTimeWithSeconds)}</span>
      }),
      columnHelper.accessor(row => row, {
        id: "importing",
        header: () => <span className="no-wrap-text">{t("v8_importing", "Importing")}</span>,
        size: 80,
        cell: ({ row: { original } }) => <Icon className={original.is_custom_query_only ? "close red" : "check green"} />
      }),

      columnHelper.accessor(row => row, {
        id: "actions",
        size: 50,
        header: () => "",
        cell: ({ row: { original } }) => {
          const allLocations = dealers.find(dealer => original.dealer_id === dealer.id)?.locations;
          const allLocationsConfigured = allLocations?.every(location => location.dms_id > DMSIDS.UNKNOWN);
          if (!allLocations || allLocationsConfigured) return null;

          return (
            <Can I={ACL_ACTIVITIES.Update} the={ACL_RESOURCES.DMSv3SettingLocation}>
              <div
                className="action-add-button-container"
                onClick={e => {
                  e.stopPropagation();
                  onSelectLocation(null);
                  onSelectLocationDealer(original);
                }}
              >
                <Icon className="plus action-button-icon-center" />
              </div>
            </Can>
          );
        }
      })
    ],
    [t]
  );

  return (
    <>
      <NavPortal>
        <SearchFilter value={searchTerm} handleSearchChange={searchTermValue => setSearchTerm(searchTermValue)} />
      </NavPortal>
      <div className="DMSv3SettingsTable">
        <ReactTable
          isFetching={isFetchingDealers}
          data={filteredDealerList}
          columns={columns}
          pageSize={PAGE_SIZE.PageSize25}
          expanded={expandedRows}
          onRowClick={onSelectDealer}
          onExpandedChange={setExpandedRows}
          renderExpandedComponent={row => (
            <DMSv3LocationsTable
              locations={row.original.dealer_locations}
              dealer={row.original}
              onSelectLocation={onSelectLocation}
              onSelectLocationDealer={onSelectLocationDealer}
            />
          )}
        />
      </div>
    </>
  );
};
